// src/BookingAllowance.js - the shopping budget attached to a booking
import React from 'react';
import { Box, Typography, Chip, LinearProgress } from '@mui/material';
import { brand, ink, surface } from './designTokens';

import { prettyStatus } from './labels';
const money = (v, currency = 'USD') =>
  `${currency === 'USD' ? '$' : ''}${parseFloat(v || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const STATUS_COLOR = {
  authorized: 'info',
  captured: 'success',
  completed: 'success',
  frozen: 'error',
  expired: 'warning',
  cancelled: 'default',
};

/**
 * What the client authorised, and what has actually been spent of it.
 *
 * The distinction matters and is easy to lose: `authorized_amount` is a hold
 * placed on their card, not a charge. `captured_amount` is what was really
 * taken. Showing one number would let someone read a £500 hold as £500 spent
 * and chase a client for money nobody has taken.
 */
export default function BookingAllowance({ budget }) {
  if (!budget) {
    return (
      <Typography variant="caption" sx={{ color: ink[500] }}>
        No shopping allowance on this booking.
      </Typography>
    );
  }

  const authorized = parseFloat(budget.authorized_amount || 0);
  const captured = parseFloat(budget.captured_amount || 0);
  const remaining = Math.max(0, authorized - captured);
  const pct = authorized > 0 ? Math.min(100, (captured / authorized) * 100) : 0;

  const expires = budget.authorization_expires_at
    ? new Date(budget.authorization_expires_at)
    : null;
  // A hold that lapses releases the money back, and the stylist can no longer
  // spend against it — worth flagging before it happens rather than after.
  const expiringSoon =
    expires && expires > new Date() && expires - new Date() < 3 * 24 * 60 * 60 * 1000;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: ink[500] }}>
          Shopping allowance
        </Typography>
        <Chip
          label={prettyStatus(budget.status)}
          size="small"
          color={STATUS_COLOR[String(budget.status).toLowerCase()] || 'default'}
        />
        {budget.frozen_reason && (
          <Typography variant="caption" color="error">
            {budget.frozen_reason}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: ink[500], display: 'block' }}>
            Authorised
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {money(authorized, budget.currency)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: ink[500], display: 'block' }}>
            Spent
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {money(captured, budget.currency)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: ink[500], display: 'block' }}>
            Remaining
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: brand.gold }}>
            {money(remaining, budget.currency)}
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: surface.line,
          '& .MuiLinearProgress-bar': { bgcolor: brand.gold },
        }}
      />

      {expires && (
        <Typography
          variant="caption"
          sx={{ mt: 0.5, display: 'block', color: expiringSoon ? 'warning.main' : ink[500] }}
        >
          {/* A hold is money set aside on the client's card, not taken from it. */}
          Hold {expires < new Date() ? 'expired' : 'expires'} {expires.toLocaleDateString()}
          {expiringSoon ? ' — spend or release it before then' : ''}
        </Typography>
      )}
    </Box>
  );
}
